import React, { useState, useEffect, useCallback } from 'react';
import './CommonStyles.css';
import { Info, Download, ChevronDown, List, Plus, Search, Trash2, Edit3, Printer, FileSpreadsheet, FileText } from 'lucide-react';
import apiService from '../../services/api';
import { toast } from 'react-toastify';

const CONNECTION_TYPES = [
    { value: 'network', label: 'Network' },
    { value: 'windows', label: 'Windows' },
    { value: 'linux', label: 'Linux' }
];

const CAPABILITY_PROFILES = [
    { value: 'default', label: 'Default' },
    { value: 'simple', label: 'Simple' },
    { value: 'SP2000', label: 'Star Branded' },
    { value: 'TEP-200M', label: 'Espon Tep' },
    { value: 'P822D', label: 'P822D' }
];

const ReceiptPrinters = () => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [printers, setPrinters] = useState([]);

    const defaultForm = {
        name: '',
        connection_type: 'network',
        capability_profile: 'default',
        char_per_line: 42,
        ip_address: '',
        port: '9100',
        path: ''
    };
    const [form, setForm] = useState({ ...defaultForm });

    const fetchPrinters = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiService.getPrinters();
            if (res.success && Array.isArray(res.data)) {
                setPrinters(res.data.map(p => ({
                    id: p.id,
                    name: p.name,
                    type: p.connection_type_label || p.connection_type,
                    ip: p.ip_address || '--',
                    port: p.port || '--',
                    raw: p
                })));
            } else {
                setPrinters([]);
            }
        } catch (err) {
      /* console.error('Error fetching printers:', err); */
            setPrinters([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchPrinters();
    }, [fetchPrinters]);

    const handleOpenAdd = () => {
        setEditingId(null);
        setForm({ ...defaultForm });
        setShowAddModal(true);
    };

    const handleOpenEdit = async (p) => {
        try {
            const res = await apiService.getPrinter(p.id);
            if (res.success && res.data && res.data.printer) {
                const pr = res.data.printer;
                setForm({
                    name: pr.name || '',
                    connection_type: pr.connection_type || 'network',
                    capability_profile: pr.capability_profile || 'default',
                    char_per_line: pr.char_per_line || 42,
                    ip_address: pr.ip_address || '',
                    port: pr.port || '',
                    path: pr.path || ''
                });
                setEditingId(p.id);
                setShowAddModal(true);
            }
        } catch (err) {
            toast.error('Failed to load printer');
        }
    };

    const handleSave = async () => {
        if (!form.name) { toast.error('Printer Name is required'); return; }
        const payload = { ...form };
        try {
            if (editingId) {
                await apiService.updatePrinter(editingId, payload);
                toast.success('Printer updated successfully');
            } else {
                await apiService.createPrinter(payload);
                toast.success('Printer added successfully');
            }
            setShowAddModal(false);
            fetchPrinters();
        } catch (err) {
            toast.error(err.message || 'Failed to save printer');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this printer?')) return;
        try {
            await apiService.deletePrinter(id);
            toast.success('Printer deleted successfully');
            fetchPrinters();
        } catch (err) {
            toast.error(err.message || 'Failed to delete printer');
        }
    };

    const filteredPrinters = printers.filter(p =>
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.ip?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const isNetwork = form.connection_type === 'network';

    const renderAddModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '8px', width: '700px', maxWidth: '95vw', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                {/* Modal Header */}
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600' }}>{editingId ? 'Edit printer' : 'Add new printer'}</h3>
                    <button onClick={() => { setShowAddModal(false); setEditingId(null); }} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.5rem', lineHeight: '1' }}>&times;</button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Printer Name:*</label>
                            <input type="text" placeholder="Printer Name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }} />
                            <small style={{ color: 'var(--etpos-text-muted, #64748b)', display: 'block', marginTop: '0.25rem' }}>Short Descriptive Name to recognize printer</small>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Connection Type:*</label>
                            <div style={{ position: 'relative' }}>
                                <select value={form.connection_type} onChange={(e) => setForm(f => ({ ...f, connection_type: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', background: 'var(--etpos-card, #fff)', appearance: 'none' }}>
                                    {CONNECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                                <ChevronDown size={16} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--etpos-text-muted, #64748b)', pointerEvents: 'none' }} />
                            </div>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Capability Profile:*</label>
                            <div style={{ position: 'relative' }}>
                                <select value={form.capability_profile} onChange={(e) => setForm(f => ({ ...f, capability_profile: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', background: 'var(--etpos-card, #fff)', appearance: 'none' }}>
                                    {CAPABILITY_PROFILES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                                <ChevronDown size={16} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--etpos-text-muted, #64748b)', pointerEvents: 'none' }} />
                            </div>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Characters per line:*</label>
                            <input type="number" value={form.char_per_line} onChange={(e) => setForm(f => ({ ...f, char_per_line: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }} />
                        </div>

                        {isNetwork ? (
                            <>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>IP Address:*</label>
                                    <input type="text" placeholder="IP Address" value={form.ip_address} onChange={(e) => setForm(f => ({ ...f, ip_address: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }} />
                                    <small style={{ color: 'var(--etpos-text-muted, #64748b)', display: 'block', marginTop: '0.25rem' }}>IP address for connecting to the printer</small>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Port:*</label>
                                    <input type="number" value={form.port} onChange={(e) => setForm(f => ({ ...f, port: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }} />
                                    <small style={{ color: 'var(--etpos-text-muted, #64748b)', display: 'block', marginTop: '0.25rem' }}>Most printer works on port 9100</small>
                                </div>
                            </>
                        ) : (
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem' }}>Path:*</label>
                                <input type="text" placeholder="Printer path" value={form.path} onChange={(e) => setForm(f => ({ ...f, path: e.target.value }))} style={{ width: '100%', padding: '0.6rem 0.8rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px' }} />
                                <small style={{ color: 'var(--etpos-text-muted, #64748b)', display: 'block', marginTop: '0.25rem' }}>Printer path (e.g. LPT1, /dev/usb/lp0)</small>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer */}
                <div style={{ padding: '1rem 1.5rem', borderTop: 'none', display: 'flex', justifyContent: 'center', gap: '0.5rem', paddingBottom: '2rem' }}>
                    <button onClick={handleSave} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '100px' }}>
                        Save
                    </button>
                    <button onClick={() => { setShowAddModal(false); setEditingId(null); }} style={{ background: '#334155', color: 'white', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '600', cursor: 'pointer', minWidth: '100px' }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ padding: '0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ background: 'var(--etpos-card, #fff)', borderRadius: '8px', border: '1px solid var(--etpos-border, #e2e8f0)', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                {/* Header Row */}
                <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '0 1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem 0' }}>
                        <Printer size={20} style={{ color: '#3b82f6' }} />
                        <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700' }}>All your receipt printers</h3>
                    </div>

                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', fontSize: '0.85rem', width: '180px', outline: 'none' }}
                            />
                        </div>
                    </div>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    {/* Controls Row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>Show</span>
                            <select style={{ padding: '0.3rem 0.5rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '4px', fontSize: '0.85rem' }}>
                                <option>25</option>
                                <option>50</option>
                                <option>100</option>
                            </select>
                            <span style={{ fontSize: '0.9rem', color: 'var(--etpos-text-muted, #64748b)' }}>entries</span>
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                                    style={{ background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text-muted, #64748b)', border: '1px solid var(--etpos-border, #e2e8f0)', padding: '0.5rem 1rem', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}
                                >
                                    <Download size={16} /> Export <ChevronDown size={14} />
                                </button>
                                {showExportDropdown && (
                                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 50, minWidth: '160px', padding: '0.5rem 0' }}>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <FileSpreadsheet size={16} color="#10b981" /> Export CSV
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <Printer size={16} color="#3b82f6" /> Print
                                        </button>
                                        <button style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.6rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                                            <FileText size={16} color="#ef4444" /> Export PDF
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button onClick={handleOpenAdd} style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1.25rem', fontWeight: '600', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                                <Plus size={18} /> Add
                            </button>
                        </div>
                    </div>

                    {/* Table View */}
                    <div style={{ overflowX: 'auto', background: 'var(--etpos-card, #fff)', borderRadius: '8px', border: '1px solid var(--etpos-border, #e2e8f0)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '2px solid var(--etpos-border, #e2e8f0)' }}>
                                    {['Printer Name', 'Connection Type', 'IP Address', 'Port', 'Action'].map((header, idx) => (
                                        <th key={idx} style={{ padding: '1rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                {header}
                                                {header !== 'Action' && <Info size={14} style={{ color: '#06b6d4' }} />}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPrinters.map((p) => (
                                    <tr key={p.id} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                                        <td style={{ padding: '1rem' }}><strong>{p.name}</strong></td>
                                        <td style={{ padding: '1rem' }}>{p.type}</td>
                                        <td style={{ padding: '1rem' }}>{p.ip}</td>
                                        <td style={{ padding: '1rem' }}>{p.port}</td>
                                        <td style={{ padding: '0.75rem 1rem' }}>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button onClick={() => handleOpenEdit(p)} style={{ background: 'var(--etpos-card, #fff)', color: '#8b5cf6', border: '1px solid #8b5cf6', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                                                    <Edit3 size={12} /> Edit
                                                </button>
                                                <button onClick={() => handleDelete(p.id)} style={{ background: 'var(--etpos-card, #fff)', color: '#ef4444', border: '1px solid #ef4444', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                                                    <Trash2 size={12} /> Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {showAddModal && renderAddModal()}
            </div>
        </div>
    );
};

export default ReceiptPrinters;
